import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import BoxDetails from '../components/boxDetails/boxDetails';
import TieMatchUp from '../components/tieMatchUp/tieMatchup';
import Loader from '../components/loader/loader';
import styles from './pages.module.scss';
import { getEnvConfig } from '../config/config';
import { DUALMATCH } from '../apollo/queries/dualMatch';
import { useQuery } from '@apollo/client';
import { createDualMatchesClient } from '../apollo/client';
import { GET_ROSTERS_BY_IDS } from '../apollo/queries/rostersByIds';
import {
  DualMatchPayload,
  IOrderOfFinish,
  TieMatchUp as TieMatchUpType
} from '../graphql-types/dualMatch';
import { Rosters, WorldTennisTeamNumbers } from '../graphql-types/roster';
import Error from '../components/error/error';

const dmClient = createDualMatchesClient();

const getMatchIndexesInOrderOfFinish = (matches: TieMatchUpType[], order: IOrderOfFinish[]) =>
  order
    .filter(orderItem => orderItem.orderOfFinish !== null)
    .sort((a, b) => a.orderOfFinish! - b.orderOfFinish!)
    .map(orderItem => {
      return matches.findIndex(match => match.id === orderItem.matchUpId) + 1;
    });

const Scorecard: React.FC = () => {
  const root = getEnvConfig().root;
  const { id } = useParams();

  const {
    loading: dmLoading,
    error: dmError,
    data: { dualMatch: data } = {}
  } = useQuery<DualMatchPayload>(DUALMATCH, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only' as const,
    client: dmClient,
    variables: {
      id: id
    }
  });

  /* Assume that "sideNumber:1" is "home" when there is no homeTeam set */
  let homeTeam = useMemo(() => data?.teams?.find(team => team.sideNumber === 1), [data?.teams]);
  let awayTeam = useMemo(() => data?.teams?.find(team => team.sideNumber === 2), [data?.teams]);

  const {
    data: rostersData,
    loading: rostersByIdsLoading,
    error: rostersByIdsError
  } = useQuery<Rosters>(GET_ROSTERS_BY_IDS, {
    variables: {
      rosterIds: [homeTeam?.id, awayTeam?.id]
    },
    skip: !(homeTeam?.id && awayTeam?.id)
  });

  /* Filter Singles/Doubles matches and sort by the collectionPosition */
  const { doublesMatches, singlesMatches, doublesOrder, singlesOrder } = useMemo(() => {
    const singlesMatches =
      data?.tieMatchUps
        ?.filter(match => match.type === 'SINGLES')
        ?.sort((a, b) => (a.collectionPosition > b.collectionPosition ? 1 : -1)) ?? [];
    const doublesMatches =
      data?.tieMatchUps
        ?.filter(match => match.type === 'DOUBLES')
        ?.sort((a, b) => (a.collectionPosition > b.collectionPosition ? 1 : -1)) ?? [];

    const { doublesOrderFinish = [], singlesOrderFinish = [] } = data || {};

    const singlesOrder = getMatchIndexesInOrderOfFinish(singlesMatches, singlesOrderFinish);
    const doublesOrder = getMatchIndexesInOrderOfFinish(doublesMatches, doublesOrderFinish);

    return {
      doublesMatches,
      singlesMatches,
      singlesOrder,
      doublesOrder
    };
  }, [data]);

  /* Work out the home and away teams
        if the match is neutral, render the teams out in the order they appear in the data (default)
        still use the concept of "home" and "away" internally to represent the left and right display of teams */
  const homeTeamNumber = data?.homeTeam?.sideNumber ?? 1;

  const rostersByIds = useMemo<Record<string, WorldTennisTeamNumbers | undefined> | undefined>(
    () =>
      rostersData?.rostersByIds?.reduce((acc, curr) => {
        return {
          ...acc,
          [curr?.groupId?.toUpperCase() as string]: curr?.worldTennisTeamNumbers?.find(
            wtn => wtn.type === 'SINGLE'
          )
        };
      }, {}),
    [rostersData]
  );

  if (data?.homeTeam !== null && data?.teams.length === 2) {
    for (const team of data.teams) {
      const teamWtn = rostersByIds?.[team?.id ?? '']?.average;

      if (team.id === data.homeTeam.id) {
        /* This is the home team */
        homeTeam = {
          ...team,
          teamWtn
        };
      } else {
        awayTeam = {
          ...team,
          teamWtn
        };
      }
    }
  }

  const renderDoubles = () => {
    return doublesMatches.map((doubleMatch: TieMatchUpType, number: number) => (
      <Grid item lg={6} xs={12} key={doubleMatch.id}>
        <TieMatchUp
          data={doubleMatch}
          number={number}
          homeTeamNumber={homeTeamNumber}
          homeTeamId={homeTeam?.id ?? ''}
          awayTeamId={awayTeam?.id ?? ''}
        />
      </Grid>
    ));
  };

  const renderSingles = () => {
    return singlesMatches.map((singleMatch: TieMatchUpType, number: number) => (
      <Grid item lg={6} xs={12} key={singleMatch.id}>
        <TieMatchUp
          data={singleMatch}
          number={number}
          homeTeamNumber={homeTeamNumber}
          homeTeamId={homeTeam?.id ?? ''}
          awayTeamId={awayTeam?.id ?? ''}
        />
      </Grid>
    ));
  };

  if (dmLoading || rostersByIdsLoading) {
    return <Loader />;
  }

  if ((dmError && !data) || (!rostersData && rostersByIdsError)) {
    return <Error />;
  }

  return (
    <div className={styles.scorecard}>
      <div className={styles.siteWidth}>
        <Link to={root} className={styles.link}>
          Back to schedule
        </Link>

        <div className={styles.box}>
          <h1>Box score</h1>

          <BoxDetails data={data} homeTeam={homeTeam} awayTeam={awayTeam} />

          <h2>Doubles</h2>

          <div className={styles.matchUps}>
            <Grid container spacing={3}>
              {renderDoubles()}
            </Grid>
            {!!doublesOrder.length && (
              <Box marginTop={2} fontSize={16} fontWeight="bold">
                {`Order of Finish: ${doublesOrder.join(', ')}`}
              </Box>
            )}
          </div>

          <div className={styles.hr}>
            <hr />
          </div>

          <h2>Singles</h2>

          <div className={styles.matchUps}>
            <Grid spacing={3} container>
              {renderSingles()}
            </Grid>
            {!!singlesOrder.length && (
              <Box marginTop={2} fontSize={16} fontWeight="bold">
                {`Order of Finish: ${singlesOrder.join(', ')}`}
              </Box>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scorecard;
